import React, { useEffect, useState } from 'react';
import { RotateCw } from 'lucide-react';
import { useTranslation } from './TranslationContext';
interface TranslatedMessageProps {
  originalText: string;
  originalLanguage: string;
  targetLanguage: string;
  showTranslationButton?: boolean;
  className?: string;
  isUserMessage?: boolean;
}
export const TranslatedMessage: React.FC<TranslatedMessageProps> = ({
  originalText,
  originalLanguage,
  targetLanguage,
  showTranslationButton = true,
  className = '',
  isUserMessage = false
}) => {
  const {
    isTranslationEnabled,
    translateMessage,
    translationInProgress
  } = useTranslation();
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationFailed, setTranslationFailed] = useState(false);
  // Translate the message when needed
  useEffect(() => {
    const performTranslation = async () => {
      if (isTranslationEnabled && originalLanguage !== targetLanguage && originalText) {
        setIsTranslating(true);
        try {
          const translated = await translateMessage(originalText, originalLanguage as any, targetLanguage as any);
          setTranslatedText(translated);
          setTranslationFailed(false);
        } catch (error) {
          console.error('Translation error:', error);
          setTranslationFailed(true);
        } finally {
          setIsTranslating(false);
        }
      } else {
        setTranslatedText(null);
      }
    };
    performTranslation();
  }, [originalText, originalLanguage, targetLanguage, isTranslationEnabled]);
  // Handle manual translation
  const handleManualTranslate = async () => {
    if (translationInProgress) return;
    setIsTranslating(true);
    try {
      const translated = await translateMessage(originalText, originalLanguage as any, targetLanguage as any);
      setTranslatedText(translated);
      setTranslationFailed(false);
    } catch (error) {
      setTranslationFailed(true);
    } finally {
      setIsTranslating(false);
    }
  };
  // Toggle between original and translated text
  const toggleOriginal = () => {
    setShowOriginal(!showOriginal);
  };
  // If translation is disabled or languages match, just show original
  if (!isTranslationEnabled || originalLanguage === targetLanguage) {
    return <span className={className} data-id="element-21568">{originalText}</span>;
  }
  // Show translation in progress
  if (isTranslating) {
    return <div className={`${className} opacity-70`} data-id="element-21569">
        <div className="flex items-center gap-2" data-id="element-21570">
          <RotateCw size={14} className="animate-spin" data-id="element-21571" />
          <span className="text-xs italic" data-id="element-21572">Translating...</span>
        </div>
        <div className="text-opacity-50" data-id="element-21573">{originalText}</div>
      </div>;
  }
  // If translation failed
  if (translationFailed) {
    return <div className={className} data-id="element-21574">
        <div data-id="element-21575">{originalText}</div>
        {showTranslationButton && <button onClick={handleManualTranslate} className="mt-1 text-xs flex items-center gap-1 text-blue-500 hover:text-blue-600 dark:hover:text-blue-400" data-id="element-21576">
            <div size={12} data-id="element-21577" />
            <span data-id="element-21578">Tap to translate (beta)</span>
          </button>}
      </div>;
  }
  // Show translated text with option to view original
  if (translatedText && !showOriginal) {
    return <div className={className} data-id="element-21579">
        <div data-id="element-21580">{translatedText}</div>
        <div className="mt-1 text-xs flex items-center gap-1 text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300" onClick={toggleOriginal} data-id="element-21581">
          <div size={12} data-id="element-21582" />
          <span data-id="element-21583">Show original</span>
        </div>
      </div>;
  }
  // Show original text with option to view translation
  return <div className={className} data-id="element-21584">
      <div data-id="element-21585">{originalText}</div>
      <div className="mt-1 text-xs flex items-center gap-1 text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300" onClick={toggleOriginal} data-id="element-21586">
        <div size={12} data-id="element-21587" />
        <span data-id="element-21588">Show translation</span>
      </div>
    </div>;
};